/**
 * Google Tag Manager utility functions
 * Push events to dataLayer for GTM to process and forward to GA4
 */
import '../types/gtm.d.ts';

/**
 * Base function to push data to the GTM dataLayer
 */
export const pushToDataLayer = (eventData: Record<string, unknown>): void => {
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventData);
    }
};

/**
 * Track successful user signup
 */
export const trackSignupComplete = (userId: string, userType: 'free' | 'paid' = 'free'): void => {
    pushToDataLayer({
        event: 'signup_complete',
        user_id: userId,
        user_type: userType,
    });
};

/**
 * Track user's first image upload
 */
export const trackFirstUpload = (userId: string): void => {
    pushToDataLayer({
        event: 'first_upload',
        user_id: userId,
    });
};

/**
 * Track when a transformation/render starts
 */
export const trackTransformationStart = (
    mode: string,
    creditsRemaining: number
): void => {
    pushToDataLayer({
        event: 'transformation_start',
        transformation_mode: mode,
        credits_remaining: creditsRemaining,
    });
};

/**
 * Track successful transformation completion
 */
export const trackTransformationComplete = (
    mode: string,
    creditsRemaining: number,
    processingTimeSeconds: number
): void => {
    pushToDataLayer({
        event: 'transformation_complete',
        transformation_mode: mode,
        credits_remaining: creditsRemaining,
        processing_time_seconds: processingTimeSeconds,
    });
};

/**
 * Track transformation errors
 */
export const trackTransformationError = (
    mode: string,
    errorType: string,
    errorMessage?: string
): void => {
    pushToDataLayer({
        event: 'transformation_error',
        transformation_mode: mode,
        error_type: errorType,
        error_message: errorMessage,
    });
};

/**
 * Track when user has low credits remaining
 */
export const trackLowCreditsWarning = (creditsRemaining: number): void => {
    pushToDataLayer({
        event: 'low_credits_warning',
        credits_remaining: creditsRemaining,
    });
};

/**
 * Track when user initiates a credit purchase
 */
export const trackCreditPurchaseInitiated = (
    packageType: string,
    value: number,
    credits: number
): void => {
    pushToDataLayer({
        event: 'credit_purchase_initiated',
        package_type: packageType,
        value: value,
        credits: credits,
    });
};

/**
 * Track successful credit purchase completion
 */
export const trackCreditPurchaseComplete = (
    transactionId: string,
    packageType: string,
    value: number,
    credits: number
): void => {
    pushToDataLayer({
        event: 'credit_purchase_complete',
        transaction_id: transactionId,
        package_type: packageType,
        value: value,
        credits: credits,
    });
};
